import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Atmosphere } from "../components/Atmosphere";
import { RotateCw, Share2, ArrowLeft, Check } from "lucide-react";

const quizData = [
  {
    id: "q1",
    de: { q: "Was zieht dich an einer geliebten Seele am stärksten an?", a: ["Ihre Verletzlichkeit, die sie nur mir zeigt.", "Ihre stille Stärke, die mich trägt."] },
    en: { q: "What draws you most to a beloved soul?", a: ["Their vulnerability, which they only show to me.", "Their silent strength, which carries me."] },
    sides: ["OW", "UW"]
  },
  {
    id: "q2",
    de: { q: "Jemand gerät in Panik. Was tust du zuerst?", a: ["Ich übernehme die Situation und gebe klare Anweisungen.", "Ich bleibe bei der Person und beruhige sie."] },
    en: { q: "Someone panics. What do you do first?", a: ["I take control of the situation and give clear instructions.", "I stay with the person and calm them down."] },
    sides: ["OW", "UW"]
  },
  {
    id: "q3",
    de: { q: "Welches Element spiegelt dein Wesen?", a: ["Erde, Feuer oder Wasser — etwas, das hält.", "Licht, Eis oder Wind — etwas, das berührt."] },
    en: { q: "Which element reflects your nature?", a: ["Earth, Fire, or Water — something that holds.", "Light, Ice, or Wind — something that touches."] },
    sides: ["OW", "UW"]
  },
  {
    id: "q4",
    de: { q: "In einer Gruppe...", a: ["Übernehme ich oft Verantwortung, auch wenn niemand fragt.", "Unterstütze ich lieber jemanden, der die Verantwortung trägt."] },
    en: { q: "In a group...", a: ["I often take responsibility, even if no one asks.", "I prefer to support someone who carries the responsibility."] },
    sides: ["OW", "UW"]
  },
  {
    id: "q5",
    de: { q: "Wie gehst du mit Stress um?", a: ["Ich funktioniere erst einmal und verarbeite Gefühle später.", "Ich muss meine Gefühle erst sortieren, bevor ich handeln kann."] },
    en: { q: "How do you handle stress?", a: ["I function first and process emotions later.", "I need to sort my feelings before I can act."] },
    sides: ["OW", "UW"]
  },
  {
    id: "q6",
    de: { q: "Du bist auf einer Feier...", a: ["Ich halte eher Abstand und beobachte.", "Ich suche Gespräche mit Menschen, die sympathisch wirken."] },
    en: { q: "You are at a party...", a: ["I tend to keep my distance and observe.", "I seek conversations with people who seem sympathetic."] },
    sides: ["OW", "UW"]
  },
  {
    id: "q7",
    de: { q: "Was gibt dir Sicherheit?", a: ["Kontrolle über die Situation.", "Vertrauen in andere Menschen."] },
    en: { q: "What gives you security?", a: ["Control over the situation.", "Trust in other people."] },
    sides: ["OW", "UW"]
  },
  {
    id: "q8",
    de: { q: "Welche Beschreibung trifft eher zu?", a: ["Ich wirke auf Fremde eher einschüchternd.", "Menschen öffnen sich mir schnell."] },
    en: { q: "Which description is more accurate?", a: ["I tend to appear intimidating to strangers.", "People open up to me quickly."] },
    sides: ["OW", "UW"]
  },
  {
    id: "q9",
    de: { q: "Nach einem anstrengenden Tag...", a: ["Möchte ich alleine sein.", "Möchte ich Zeit mit vertrauten Menschen verbringen."] },
    en: { q: "After a grueling day...", a: ["I want to be alone.", "I want to spend time with trusted people."] },
    sides: ["OW", "UW"]
  },
  {
    id: "q10",
    de: { q: "Wenn jemand unfair behandelt wird...", a: ["Ich stelle mich schützend davor.", "Ich kümmere mich um die verletzte Person."] },
    en: { q: "When someone is treated unfairly...", a: ["I stand protectively in front of them.", "I take care of the injured person."] },
    sides: ["OW", "UW"]
  }
];

export default function Quiz() {
  const [currentLang, setCurrentLang] = useState(() => {
    return localStorage.getItem("project_lang") || "de";
  });

  useEffect(() => {
    const handleLangChange = () => {
      setCurrentLang(localStorage.getItem("project_lang") || "de");
    };
    window.addEventListener("languageChange", handleLangChange);
    return () => window.removeEventListener("languageChange", handleLangChange);
  }, []);

  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [copied, setCopied] = useState(false);

  const results = {
    OW: {
      title: currentLang === "en" ? "You are OB" : "Du bist OW",
      chinese: "上",
      color: "#C9A227",
      text: currentLang === "en"
        ? "Your heart holds others like a Golden Dragon holds its mountain peak. You do not lead because you must — but because your silence brings peace to others. In Ruìhuá, you would find your reflection in Zhènyáng or Chìyàn."
        : "Dein Herz hält andere wie ein Goldener Drache seine Bergspitze. Du führst nicht, weil du musst — sondern weil deine Stille andere zur Ruhe bringt. In Ruìhuá fändest du dein Spiegelbild in Zhènyáng oder Chìyàn.",
    },
    UW: {
      title: currentLang === "en" ? "You are UB" : "Du bist UW",
      chinese: "下",
      color: "#8B1111",
      text: currentLang === "en"
        ? "Your heart burns in an open dance with others. You seek connection like the moon seeks water — and within this quest lies your true power. In Ruìhuá, you would find your reflection in Lùhuá or Jinwoo."
        : "Dein Herz brennt im offenen Tanz mit anderen. Du sucht Verbindung wie der Mond das Wasser sucht — und in dieser Suche liegt deine wahre Macht. In Ruìhuá fändest du dein Spiegelbild in Lùhuá oder Jinwoo.",
    },
  };

  const submit = (side) => {
    setAnswers((prev) => [...prev, side]);
    setStep((prev) => prev + 1);
  };

  const goBack = () => {
    if (step > 0) {
      setStep((prev) => prev - 1);
      setAnswers((prev) => prev.slice(0, -1));
    }
  };

  const reset = () => {
    setStep(0);
    setAnswers([]);
    setCopied(false);
  };

  const ow = answers.filter((a) => a === "OW").length;
  const uw = answers.filter((a) => a === "UW").length;
  const totalAnswers = answers.length;
  const result = ow >= uw ? results.OW : results.UW;

  const handleShare = () => {
    const textToCopy = currentLang === "en"
      ? `I am ${result.title} in Ruìhuá. ${window.location.origin}/test`
      : `Ich bin ${result.title} in Ruìhuá. ${window.location.origin}/test`;

    navigator.clipboard?.writeText(textToCopy).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const rawQuestion = step < quizData.length ? quizData[step] : null;

  return (
    <div className="pt-32 pb-24 px-6 min-h-0 h-auto overflow-hidden relative" data-testid="quiz-page">
      <Atmosphere variant="minimal" />
      <div className="max-w-3xl mx-auto relative z-10">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="text-center mb-12"
        >
          <p className="chinese text-[#C9A227]/70 text-2xl mb-3">心鏡</p>
          <h1 className="serif text-5xl lg:text-6xl text-[#E8DDC8] mb-3">
            {currentLang === "en" ? "The Mirror of the Soul" : "Der Spiegel der Seele"}
          </h1>
          <p className="serif italic text-[#B3B6B7]">
            {currentLang === "en" ? "Are you OB or UB?" : "Bist du OW oder UW?"}
          </p>
        </motion.div>

        {/* Quiz-Modus */}
        {step < quizData.length && rawQuestion && (
          <>
            {/* Fortschritt & Zurück */}
            <div className="flex items-center gap-4 mb-12 justify-center relative">
              {step > 0 && (
                <button 
                  onClick={goBack}
                  className="absolute left-0 text-[#B3B6B7] hover:text-white transition-colors flex items-center gap-1 text-sm serif italic"
                >
                  <ArrowLeft size={16} /> {currentLang === "en" ? "back" : "zurück"}
                </button>
              )}
              <div className="flex items-center gap-3">
                {quizData.map((item, i) => (
                  <span
                    key={`bar-${item.id}`}
                    className={`h-px transition-all duration-300 ${i <= step ? "w-12 bg-[#C9A227]" : "w-6 bg-[#C9A227]/20"}`}
                  />
                ))}
              </div>
            </div>

            {/* Frage-Card: Hülle bleibt IMMER fest stehen, kein unmount */}
            <div className="card-imperial p-10" data-testid={`question-${step}`}>
              {/* Nur der innere Inhalt animiert ganz sanft die Deckkraft bei jedem Schritt */}
              <motion.div
                key={rawQuestion.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.25 }}
              >
                <p className="text-xs tracking-[0.3em] uppercase text-[#C9A227] mb-6">
                  {currentLang === "en" 
                    ? `Question ${step + 1} of ${quizData.length}` 
                    : `Frage ${step + 1} von ${quizData.length}`
                  }
                </p>
                <h2 className="serif text-3xl text-[#E8DDC8] leading-snug mb-10">
                  {rawQuestion[currentLang].q}
                </h2>
                <div className="space-y-4">
                  {rawQuestion[currentLang].a.map((text, i) => (
                    <button
                      key={`opt-${rawQuestion.id}-${i}`}
                      onClick={() => submit(rawQuestion.sides[i])}
                      data-testid={`answer-${step}-${i}`}
                      className="w-full text-left p-5 border border-[#C9A227]/25 hover:border-[#C9A227] hover:bg-[#C9A227]/5 transition-all group"
                    >
                      <span className="serif text-lg text-[#E8DDC8] group-hover:text-white">
                        {text}
                      </span>
                    </button>
                  ))}
                </div>
              </motion.div>
            </div>
          </>
        )}

        {/* Ergebnis-Modus */}
        {step === quizData.length && (
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="tarot-card p-10 text-center"
            data-testid="quiz-result"
            style={{ borderColor: `${result.color}88` }}
          >
            <div className="corner-orn tl" style={{ borderColor: result.color }} />
            <div className="corner-orn br" style={{ borderColor: result.color }} />
            <p className="chinese text-7xl mb-4" style={{ color: result.color }}>{result.chinese}</p>
            <h2 className="serif text-5xl text-[#E8DDC8] mb-3">{result.title}</h2>
            
            {/* Punkte */}
            <p className="text-xs tracking-[0.3em] uppercase mb-8" style={{ color: result.color }}>
              {ow} · OW / UW · {uw}
            </p>

            {/* Spektrum */}
            <div className="my-10 px-2 relative max-w-md mx-auto">
              <div className="flex justify-between items-center text-xs tracking-widest uppercase mb-1 text-[#B3B6B7]">
                <span style={{ color: results.OW.color }} className="font-semibold text-[10px]">OW</span>
                <span style={{ color: results.UW.color }} className="font-semibold text-[10px]">UW</span>
              </div>

              <div className="relative w-full h-3 mb-1">
                {totalAnswers > 0 && (
                  <motion.div
                    className="absolute top-0 -ml-1 w-0 h-0"
                    style={{
                      borderLeft: "5px solid transparent",
                      borderRight: "5px solid transparent",
                      borderTop: "7px solid #B3B6B7",
                    }}
                    initial={{ left: "50%" }}
                    animate={{ 
                      left: `${((totalAnswers - ow + uw) / (totalAnswers * 2)) * 100}%`
                    }}
                    transition={{ duration: 1.5, ease: "easeOut" }}
                  />
                )}
              </div>

              <div 
                className="h-[3px] w-full relative"
                style={{
                  background: `linear-gradient(to right, ${results.OW.color}, #B45309 50%, ${results.UW.color})`
                }}
              >
                <div className="absolute left-1/2 -translate-x-1/2 -top-0.5 h-2 w-[1px] bg-[#E8DDC8]/40 z-20" />
              </div>
            </div>

            <p className="serif italic text-lg text-[#B3B6B7] max-w-xl mx-auto leading-relaxed mb-10">
              {result.text}
            </p>
            
            {/* Buttons */}
            <div className="flex flex-wrap items-center justify-center gap-4">
              <button onClick={reset} className="btn-imperial" data-testid="reset-quiz">
                <RotateCw size={14} /> {currentLang === "en" ? "Mirror again" : "Erneut spiegeln"}
              </button>
              <button
                className="btn-ghost flex items-center gap-2"
                data-testid="share-result"
                onClick={handleShare}
              >
                {copied ? (
                  <>
                    <Check size={14} className="text-green-400" /> {currentLang === "en" ? "Link copied!" : "Link kopiert!"}
                  </>
                ) : (
                  <>
                    <Share2 size={14} /> {currentLang === "en" ? "Share result" : "Ergebnis teilen"}
                  </>
                )}
              </button>
            </div>
          </motion.div>
        )}

      </div>
    </div>
  );
}